package fr.manyverse.app.presentation.components.gm_screen

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Announcement
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Dialpad
import androidx.compose.material.icons.filled.Draw
import androidx.compose.material.icons.filled.Key
import androidx.compose.material.icons.filled.Timer
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.ActiveLockpicking
import fr.manyverse.app.domain.model.ActiveTimer
import fr.manyverse.app.domain.model.ActiveWhiteboard
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.GameModule
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.presentation.components.rememberImageSelection
import fr.manyverse.app.presentation.components.modules.announcement.AnnouncementCreationDialog
import fr.manyverse.app.presentation.components.modules.announcement.AnnouncementGmSection
import fr.manyverse.app.presentation.components.modules.digicode.DigicodeCreationDialog
import fr.manyverse.app.presentation.components.modules.digicode.DigicodeGmSection
import fr.manyverse.app.presentation.components.modules.gm_timer.GmTimerLaunchDialog
import fr.manyverse.app.presentation.components.modules.lockpicking.LockpickingCreationDialog
import fr.manyverse.app.presentation.components.modules.lockpicking.LockpickingGmSection
import fr.manyverse.app.presentation.components.modules.timer.TimerGmSection
import fr.manyverse.app.presentation.components.modules.timer.TimerLaunchDialog
import fr.manyverse.app.presentation.components.modules.whiteboard.WhiteboardCreationDialog
import fr.manyverse.app.presentation.state.AsyncUiState
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.icon_sword_cross
import manyverse.shared.generated.resources.module_announcement
import manyverse.shared.generated.resources.module_announcement_desc
import manyverse.shared.generated.resources.module_combat
import manyverse.shared.generated.resources.module_combat_desc
import manyverse.shared.generated.resources.module_digicode
import manyverse.shared.generated.resources.module_digicode_desc
import manyverse.shared.generated.resources.module_gm_timer
import manyverse.shared.generated.resources.module_gm_timer_desc
import manyverse.shared.generated.resources.module_lockpicking
import manyverse.shared.generated.resources.module_lockpicking_desc
import manyverse.shared.generated.resources.module_timer
import manyverse.shared.generated.resources.module_timer_desc
import manyverse.shared.generated.resources.module_whiteboard
import manyverse.shared.generated.resources.module_whiteboard_desc
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

private class MultipleParticipantSelectionState {
    var selectedIds by mutableStateOf<Set<String>>(emptySet())
        private set
    var allSelected by mutableStateOf(true)
        private set

    fun toggleAll() {
        allSelected = !allSelected
        if (allSelected) selectedIds = emptySet()
    }

    fun toggle(userId: String) {
        selectedIds = if (userId in selectedIds) selectedIds - userId else selectedIds + userId
        if (selectedIds.isNotEmpty()) allSelected = false
    }

    fun reset() {
        selectedIds = emptySet()
        allSelected = true
    }

    fun resolve(players: List<TableMember>): List<String> =
        if (allSelected) players.map { it.userId } else selectedIds.toList()
}

/**
 * Data class representing a module available in the game.
 */
data class ModuleItem(
    val id: String,
    val name: String,
    val description: String,
    val icon: Painter
)

@Composable
fun ModulesTab(
    availableModules: Set<GameModule>,
    tableMembers: List<TableMember>,
    characters: List<Character>,
    onCreateAnnouncement: (
        text: String?,
        image: ImageData?,
        targetParticipants: List<String>
    ) -> Unit,
    announcementState: AsyncUiState<*>,
    activeAnnouncements: List<ActiveAnnouncement>,
    onCancelAnnouncement: (announcementEventId: String) -> Unit,
    // Whiteboard props
    whiteboardLaunchState: AsyncUiState<ActiveWhiteboard>,
    whiteboardIsMinimized: Boolean,
    onOpenWhiteboard: (List<String>, Boolean) -> Unit,
    onRestoreWhiteboard: () -> Unit,
    onResetWhiteboardLaunchState: () -> Unit,
    // Timer props
    timerLaunchState: AsyncUiState<ActiveTimer>,
    activeTimers: List<ActiveTimer>,
    onCreateTimer: (durationSeconds: Int, targetParticipants: List<String>) -> Unit,
    onStopTimer: (String) -> Unit,
    onTogglePauseTimer: (String) -> Unit,
    onTimerExpired: (String) -> Unit,
    onResetTimerLaunchState: () -> Unit,
    // Digicode props
    digicodeState: AsyncUiState<ActiveDigicode>,
    activeDigicodes: List<ActiveDigicode>,
    onCreateDigicode: (
        targetParticipantId: String,
        digitCount: Int,
        maxAttempts: Int,
        timeLimitSeconds: Int?,
        showAttemptsToPlayer: Boolean,
        soundEnabled: Boolean,
        solution: String
    ) -> Unit,
    onCancelDigicode: (digicodeId: String) -> Unit,
    onCloseDigicode: (digicodeId: String) -> Unit,
    onResetDigicodeLaunchState: () -> Unit,
    // Lockpicking props
    lockpickingState: AsyncUiState<ActiveLockpicking>,
    activeLockpicking: ActiveLockpicking?,
    onCreateLockpicking: (
        targetParticipantId: String,
        lockCount: Int,
        timeLimitSeconds: Int?,
        showLockCount: Boolean,
        soundEnabled: Boolean
    ) -> Unit,
    onCancelLockpicking: () -> Unit,
    onCloseLockpicking: () -> Unit,
    onResetLockpickingLaunchState: () -> Unit,
    onCreateGmTimer: (durationSeconds: Int, continueAfterEnd: Boolean) -> Unit,
    // Combat props
    combatIsActive: Boolean,
    onOpenCombatPrepare: () -> Unit,
    onRestoreCombat: () -> Unit,
    modifier: Modifier = Modifier
) {
    // State for announcement dialog
    var showAnnouncementDialog by remember { mutableStateOf(false) }
    var announcementText by remember { mutableStateOf("") }
    val announcementImage = rememberImageSelection()
    val announcementParticipants = remember { MultipleParticipantSelectionState() }

    // State for whiteboard dialog
    var showWhiteboardDialog by remember { mutableStateOf(false) }
    val whiteboardParticipants = remember { MultipleParticipantSelectionState() }
    var whiteboardShowGrid by remember { mutableStateOf(false) }

    // State for timer dialog
    var showTimerDialog by remember { mutableStateOf(false) }
    val timerParticipants = remember { MultipleParticipantSelectionState() }

    // State for digicode dialog
    var showDigicodeDialog by remember { mutableStateOf(false) }

    // State for lockpicking dialog
    var showLockpickingDialog by remember { mutableStateOf(false) }

    // State for GM timer dialog
    var showGmTimerDialog by remember { mutableStateOf(false) }

    // Players only (not GM) who have chosen a character
    val players = remember(tableMembers) {
        tableMembers.filter { it.role == MemberRole.PLAYER && it.characterId != null }
    }
    val labelOf: (TableMember) -> String = { member ->
        characters.find { it.userId == member.userId }?.name ?: member.userId.take(8)
    }

    // Close dialog on success
    LaunchedEffect(announcementState) {
        if (announcementState is AsyncUiState.Success) {
            showAnnouncementDialog = false
            // Reset form
            announcementText = ""
            announcementImage.reset()
            announcementParticipants.reset()
        }
    }

    // Handle timer launch success
    LaunchedEffect(timerLaunchState) {
        if (timerLaunchState is AsyncUiState.Success) {
            showTimerDialog = false
            timerParticipants.reset()
            onResetTimerLaunchState()
        }
    }

    // Handle digicode launch success
    LaunchedEffect(digicodeState) {
        if (digicodeState is AsyncUiState.Success) {
            showDigicodeDialog = false
            onResetDigicodeLaunchState()
        }
    }

    // Handle lockpicking launch success
    LaunchedEffect(lockpickingState) {
        if (lockpickingState is AsyncUiState.Success) {
            showLockpickingDialog = false
            onResetLockpickingLaunchState()
        }
    }

    // Handle whiteboard open success
    LaunchedEffect(whiteboardLaunchState) {
        if (whiteboardLaunchState is AsyncUiState.Success) {
            showWhiteboardDialog = false
            whiteboardParticipants.reset()
            whiteboardShowGrid = false
            onResetWhiteboardLaunchState()
        }
    }

    // Define available modules
    val allModules = listOf(
        GameModule.ANNOUNCEMENT to ModuleItem(
            id = "announcement",
            name = stringResource(Res.string.module_announcement),
            description = stringResource(Res.string.module_announcement_desc),
            icon = rememberVectorPainter(Icons.AutoMirrored.Filled.Announcement)
        ),
        GameModule.WHITEBOARD to ModuleItem(
            id = "whiteboard",
            name = stringResource(Res.string.module_whiteboard),
            description = stringResource(Res.string.module_whiteboard_desc),
            icon = rememberVectorPainter(Icons.Default.Draw)
        ),
        GameModule.TIMER to ModuleItem(
            id = "timer",
            name = stringResource(Res.string.module_timer),
            description = stringResource(Res.string.module_timer_desc),
            icon = rememberVectorPainter(Icons.Default.Timer)
        ),
        GameModule.DIGICODE to ModuleItem(
            id = "digicode",
            name = stringResource(Res.string.module_digicode),
            description = stringResource(Res.string.module_digicode_desc),
            icon = rememberVectorPainter(Icons.Default.Dialpad)
        ),
        GameModule.LOCKPICKING to ModuleItem(
            id = "lockpicking",
            name = stringResource(Res.string.module_lockpicking),
            description = stringResource(Res.string.module_lockpicking_desc),
            icon = rememberVectorPainter(Icons.Default.Key)
        ),
        GameModule.GM_TIMER to ModuleItem(
            id = "gm_timer",
            name = stringResource(Res.string.module_gm_timer),
            description = stringResource(Res.string.module_gm_timer_desc),
            icon = rememberVectorPainter(Icons.Default.Timer)
        ),
        GameModule.COMBAT to ModuleItem(
            id = "combat",
            name = stringResource(Res.string.module_combat),
            description = stringResource(Res.string.module_combat_desc),
            icon = painterResource(Res.drawable.icon_sword_cross)
        )
    )
    val modules = allModules
        .filter { (module, _) -> module in availableModules }
        .map { (_, item) -> item }

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
    ) {
        @OptIn(ExperimentalLayoutApi::class)
        FlowRow(
            modifier = Modifier.fillMaxSize(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            modules.sortedBy { it.name.lowercase() }.forEach { module ->
                ModuleCard(
                    module = module,
                    onClick = {
                        when (module.id) {
                            "announcement" -> showAnnouncementDialog = true
                            "whiteboard" -> if (whiteboardIsMinimized) onRestoreWhiteboard() else showWhiteboardDialog = true
                            "timer" -> showTimerDialog = true
                            "digicode" -> showDigicodeDialog = true
                            "lockpicking" -> showLockpickingDialog = true
                            "gm_timer" -> showGmTimerDialog = true
                            "combat" -> if (combatIsActive) onRestoreCombat() else onOpenCombatPrepare()
                        }
                    }
                )
            }
        }

        // Active announcement section (GM view)
        AnnouncementGmSection(
            activeAnnouncements = activeAnnouncements,
            characters = characters,
            onCancel = onCancelAnnouncement
        )

        // Active timers section (GM view)
        TimerGmSection(
            activeTimers = activeTimers,
            onStopTimer = onStopTimer,
            onTogglePauseTimer = onTogglePauseTimer,
            onTimerExpired = onTimerExpired
        )

        // Active digicode section (GM view) — one card per active digicode
        if (GameModule.DIGICODE in availableModules) {
            activeDigicodes.forEach { dc ->
                val targetName = characters.find { it.userId == dc.targetParticipantId }?.name
                    ?: dc.targetParticipantId.take(8)
                DigicodeGmSection(
                    activeDigicode = dc,
                    targetPlayerName = targetName,
                    onCancel = onCancelDigicode,
                    onClose = onCloseDigicode
                )
            }
        }

        // Active lockpicking section (GM view)
        val lockpickingTargetName = activeLockpicking?.let { lp ->
            characters.find { it.userId == lp.targetParticipantId }?.name
                ?: lp.targetParticipantId.take(8)
        } ?: ""
        LockpickingGmSection(
            activeLockpicking = activeLockpicking,
            targetPlayerName = lockpickingTargetName,
            onCancel = onCancelLockpicking,
            onClose = onCloseLockpicking
        )
    }

    // Announcement Dialog
    AnnouncementCreationDialog(
        showDialog = showAnnouncementDialog,
        players = players,
        labelOf = labelOf,
        selectedText = announcementText,
        selectedImageData = announcementImage.selected,
        selectedParticipantIds = announcementParticipants.selectedIds,
        allSelected = announcementParticipants.allSelected,
        isLoading = announcementState is AsyncUiState.Loading,
        errorMessage = (announcementState as? AsyncUiState.Error)?.message,
        onTextChange = { announcementText = it },
        onPickImage = announcementImage::pick,
        onClearImage = { announcementImage.reset() },
        onToggleAllSelected = { announcementParticipants.toggleAll() },
        onToggleParticipant = { announcementParticipants.toggle(it) },
        onDismiss = {
            showAnnouncementDialog = false
            announcementText = ""
            announcementImage.reset()
            announcementParticipants.reset()
        },
        onConfirm = {
            onCreateAnnouncement(
                announcementText.takeIf { it.isNotBlank() },
                announcementImage.selected,
                announcementParticipants.resolve(players)
            )
        }
    )

    // Whiteboard Open Dialog
    WhiteboardCreationDialog(
        showDialog = showWhiteboardDialog,
        players = players,
        labelOf = labelOf,
        selectedParticipantIds = whiteboardParticipants.selectedIds,
        allSelected = whiteboardParticipants.allSelected,
        isLoading = whiteboardLaunchState is AsyncUiState.Loading,
        errorMessage = (whiteboardLaunchState as? AsyncUiState.Error)?.message,
        onToggleAllSelected = { whiteboardParticipants.toggleAll() },
        onToggleParticipant = { whiteboardParticipants.toggle(it) },
        showGrid = whiteboardShowGrid,
        onToggleShowGrid = { whiteboardShowGrid = !whiteboardShowGrid },
        onDismiss = {
            showWhiteboardDialog = false
            whiteboardParticipants.reset()
            whiteboardShowGrid = false
            onResetWhiteboardLaunchState()
        },
        onConfirm = {
            onOpenWhiteboard(whiteboardParticipants.resolve(players), whiteboardShowGrid)
        }
    )

    // Digicode Launch Dialog
    if (GameModule.DIGICODE in availableModules) {
        val alreadyTargetedIds = remember(activeDigicodes) {
            activeDigicodes.map { it.targetParticipantId }.toSet()
        }
        DigicodeCreationDialog(
            showDialog = showDigicodeDialog,
            players = players.filter { it.userId !in alreadyTargetedIds },
            labelOf = labelOf,
            isLoading = digicodeState is AsyncUiState.Loading,
            errorMessage = (digicodeState as? AsyncUiState.Error)?.message,
            onDismiss = {
                showDigicodeDialog = false
                onResetDigicodeLaunchState()
            },
            onConfirm = { targetId, digitCount, maxAttempts, timeLimitSeconds, showAttempts, soundEnabled, solution ->
                onCreateDigicode(
                    targetId,
                    digitCount,
                    maxAttempts,
                    timeLimitSeconds,
                    showAttempts,
                    soundEnabled,
                    solution
                )
            }
        )
    }

    // Lockpicking Launch Dialog
    val lockpickingAlreadyTargetedId = activeLockpicking?.targetParticipantId
    LockpickingCreationDialog(
        showDialog = showLockpickingDialog,
        players = if (lockpickingAlreadyTargetedId != null) players.filter { it.userId != lockpickingAlreadyTargetedId } else players,
        labelOf = labelOf,
        isLoading = lockpickingState is AsyncUiState.Loading,
        errorMessage = (lockpickingState as? AsyncUiState.Error)?.message,
        onDismiss = {
            showLockpickingDialog = false
            onResetLockpickingLaunchState()
        },
        onConfirm = { targetId, lockCount, timeLimitSeconds, showLockCount, soundEnabled ->
            onCreateLockpicking(targetId, lockCount, timeLimitSeconds, showLockCount, soundEnabled)
        }
    )

    // Timer Launch Dialog
    TimerLaunchDialog(
        showDialog = showTimerDialog,
        players = players,
        labelOf = labelOf,
        selectedParticipantIds = timerParticipants.selectedIds,
        allSelected = timerParticipants.allSelected,
        isLoading = timerLaunchState is AsyncUiState.Loading,
        errorMessage = (timerLaunchState as? AsyncUiState.Error)?.message,
        onToggleAllSelected = { timerParticipants.toggleAll() },
        onToggleParticipant = { timerParticipants.toggle(it) },
        onDismiss = {
            showTimerDialog = false
            timerParticipants.reset()
            onResetTimerLaunchState()
        },
        onConfirm = { durationSeconds ->
            onCreateTimer(durationSeconds, timerParticipants.resolve(players))
        }
    )

    // GM Timer Launch Dialog
    GmTimerLaunchDialog(
        showDialog = showGmTimerDialog,
        onDismiss = { showGmTimerDialog = false },
        onConfirm = { durationSeconds, continueAfterEnd ->
            onCreateGmTimer(durationSeconds, continueAfterEnd)
            showGmTimerDialog = false
        }
    )
}

@Composable
private fun ModuleCard(
    module: ModuleItem,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier
            .width(400.dp)
            .clickable(onClick = onClick),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surface
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Icon(
                painter = module.icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(40.dp)
            )

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = module.name,
                    style = MaterialTheme.typography.titleMedium
                )
                Text(
                    text = module.description,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}
